import {
  type DragEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useState,
} from 'react'

import { useTranslation } from 'react-i18next'

import {
  Box,
  HStack,
  LightModeSemanticColors,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import {
  DeleteIconButton,
  EditIconButton,
} from '~/components/common/MasterDataTable/RowActionButtons'
import {
  ROW_ACTION_GAP,
  STICKY_ACTION_HOVER_OVERLAY,
  stickyActionCellStyle,
  stickyActionHeaderStyle,
  useStickyRightShadow,
} from '~/components/common/MasterDataTable/stickyActionColumn'
import {
  RegionLoadingOverlay,
  useDelayedLoading,
} from '~/components/common/RegionLoading'
import {
  TABLE_MESSAGE_ROW_MIN_HEIGHT,
  TableMessageRow,
} from '~/components/common/TableMessageRow'

export { AuditCell } from '~/components/common/MasterDataTable/AuditCell'
export {
  DeleteIconButton,
  DetailIconButton,
  DuplicateIconButton,
  EditIconButton,
} from '~/components/common/MasterDataTable/RowActionButtons'

export type MasterDataTableColumn<T> = {
  key: string
  header: ReactNode
  align?: 'left' | 'right' | 'center'
  /**
   * true のとき td レベルで `whiteSpace: nowrap` を当てる (= セル内で折り返さない)。
   * 監査情報 (日時 + 更新者) など 1 行に保ちたい列で使う。 未指定時は折り返し可 (normal)。
   * cell 側で nowrap を再現する代わりにこの契約を使う。
   */
  nowrap?: boolean
  /**
   * 値の整形パターン。 `'number'` は td レベルで `fontVariantNumeric: tabular-nums`
   * (等幅数字) を当て、 `align` 未指定時は右寄せにする (= 数値列の桁位置を揃える)。
   * cell は整形済みの表示値 (例: `n.toLocaleString()`) を返す
   * (= tabular-nums span で包む代わりにこの契約を使う)。 実在する整形は数値のみ。
   */
  format?: 'number'
  /** 並べ替えられる列の th に付ける `aria-sort`。 見出しは `SortableColumnHeader` を使う。 */
  ariaSort?: 'ascending' | 'descending' | 'none'
  cell: (row: T) => ReactNode
}

export type MasterDataTableDragHandleColumn<T> = {
  key: string
  header?: ReactNode
  width?: string
  /** drag handle セルの中身を描画する。 canDrag は呼び出し側で制御する。 */
  render: (row: T, context: { canDrag: boolean }) => ReactNode
}

type Props<T> = {
  columns: Array<MasterDataTableColumn<T>>
  rows: T[]
  getRowKey: (row: T) => string
  /**
   * 取得中か。行を出さずに 88px の高さだけ保ち、300ms を過ぎたら表の外枠を影 + スピナーで覆う
   * (領域のローディング = [[RegionLoading]] の規則。読み込み中の文言の行は出さない)。
   */
  isLoading?: boolean
  /**
   * empty 行の文言。 未指定時は `common:table.empty` を使う。
   */
  emptyLabel?: string
  /**
   * error 行の文言。 未指定時はエラー行を出さない。
   */
  errorLabel?: string
  /**
   * 定義時のみ行 hover + cursor:pointer + click handler を有効化する。
   * 設計 HTML の `tbody tr { cursor: pointer; }` 相当。
   * 第 2 引数の MouseEvent を使うことで cmd/ctrl+クリックで別タブ遷移を扱える。
   */
  onRowClick?: (row: T, event?: MouseEvent) => void
  /**
   * true の時、 `onRowClick` のある行を Tab で focus でき、 Enter で `onRowClick` を呼ぶ
   * (event は渡さない)。 行の中のボタン等で押した Enter と IME の変換確定は無視する。
   * 既定は off (既存の表は行に focus が止まらない)。
   */
  rowKeyboardActivation?: boolean
  /**
   * 定義時のみ末尾に action 列を自動で描画する。
   * 内部で stopPropagation を当てるため、 ここに置いたボタンの click は
   * `onRowClick` を発火しない。
   */
  renderRowActions?: (row: T) => ReactNode
  /**
   * action 列の幅。 複数ボタンを置く場合に広げる。 未指定時は 110px。
   */
  actionColWidth?: string
  /**
   * AC-011: 行を disabled 表示にする判定関数。
   * true を返す行は opacity: 0.5 + cursor: not-allowed で描画され、
   * onRowClick を発火せず、 renderRowActions も描画しない。
   */
  isRowDisabled?: (row: T) => boolean
  /**
   * 定義時のみ先頭に drag handle 列を描画し、行をドラッグ可能にする。
   */
  dragHandleColumn?: MasterDataTableDragHandleColumn<T>
  /**
   * drag handle 列がある時に行のドラッグを有効化する。
   * フィルタ適用中等は false にしてドラッグを無効化する。
   */
  canDragRows?: boolean
  onRowDragStart?: (row: T) => void
  onRowDragEnd?: () => void
  onRowDragOver?: (row: T, event: DragEvent<HTMLTableRowElement>) => void
  onRowDrop?: (row: T, event: DragEvent<HTMLTableRowElement>) => void
  /** 行に追加のインライン style を与える (drag over 時の視覚フィードバック等)。 */
  getRowStyle?: (row: T) => React.CSSProperties | undefined
}

const DEFAULT_actionColWidth = '110px'

// 列 → 水平揃えの純写像。 明示 align を最優先し、 未指定の数値列 (format:'number') は
// 桁位置を揃えるため右寄せに落とす (= 各 table が align:'right' を書き足さずに済む)。
const textAlignFor = <T,>(
  col: MasterDataTableColumn<T>,
): React.CSSProperties['textAlign'] =>
  col.align ?? (col.format === 'number' ? 'right' : 'left')

// sticky header 背景 (éclair token: Surface.tableHeader = #e7eef2、
// 設計 HTML `var(--surface-table-header)` 相当)。 ecloud 0.14.2 は同名 token を
// 持つため LightModeSemanticColors 経由で参照する。 生 table を使う他ページ
// (SpotPrice/StatsTable・KomaTable, SpotBidding/tabs/NormalBidTab) から
// import して集約する single source。
export const TABLE_HEADER_BG = LightModeSemanticColors.Surface.tableHeader.value

/**
 * TH (表見出しセル) の canonical な見た目。 design `css/components.css` の
 * `.ecl-table th` (地 = `--surface-table-header` / 文字 = `--text-mid-emphasis` /
 * 600 / padding 10px 12px = DS canonical の高さ 40px を padding で確保) と 1:1。
 *
 * [DS差異] ecloud 0.14.2 の `TableColumnHeader` は素の chakra 既定のままで
 * (実測: 地 transparent / 色 #09090b / weight 500 / padding 8px)、 éclair の TH
 * 規定を持たない。 そのため各表がこの style を自前で当てる必要がある。 画面ごとに
 * 書き写すと表ごとに見出しの見た目がずれるため、 **この 1 箇所を single source**
 * とし、 生 table を使う画面 (SpotBidding の入札一覧 / 履歴一覧など) からも
 * import して使う。
 *
 * `textAlign` / `width` は列ごとに変わるので**含めない** (呼び出し側が指定する)。
 */
export const TABLE_HEADER_CELL_STYLE: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  background: TABLE_HEADER_BG,
  // design `.ecl-table th` (= DS canonical 40px 高さ) を padding で確保
  padding: '10px 12px',
  fontWeight: 600,
  fontSize: fontSizes.md.value,
  borderBottom: `1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`,
  zIndex: 1,
  whiteSpace: 'nowrap',
  textTransform: 'none',
  letterSpacing: 'normal',
  // design: TH は TD より一段控えめな mid-emphasis
  color: LightModeSemanticColors.Text.midEmphasis.value,
}

/**
 * 本文セル (TD) の見た目。 design `.ecl-table td` (`css/components.css`: `padding: 6px 10px` /
 * `border-bottom: 1px solid --border-low`) に対応する。 本 component 内の `tdBaseStyle` と、
 * 生 table を使う画面 (取引詳細モーダルの leg テーブル等) の両方から使う。
 *
 * size md の表の値の正は `~/constants/system.ts` の table recipe で、 本定数は同じ値を持つ。
 * 本定数が要るのは recipe の md が効かない `size="sm"` の表 (Chakra 既定の sm は root が
 * `textStyle: "sm"` = 12px で、 cell の padding も別) の TD を md と同じにする時。
 * `fontSize` を持つのは **継承に依存せず単体で design と一致させる**ため。
 *
 * `textAlign` / `width` は列ごとに変わるので**含めない** (呼び出し側が指定する)。
 * ここに入れると inline style が chakra prop に勝ち、 列ごとの揃えが効かなくなる。
 */
export const TABLE_BODY_CELL_STYLE: React.CSSProperties = {
  padding: '6px 10px',
  borderBottom: `1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`,
  fontSize: fontSizes.md.value,
}

export const MasterDataTable = <T,>({
  columns,
  rows,
  getRowKey,
  isLoading = false,
  emptyLabel,
  errorLabel,
  onRowClick,
  rowKeyboardActivation = false,
  renderRowActions,
  actionColWidth = DEFAULT_actionColWidth,
  isRowDisabled,
  dragHandleColumn,
  canDragRows,
  onRowDragStart,
  onRowDragEnd,
  onRowDragOver,
  onRowDrop,
  getRowStyle,
}: Props<T>): React.ReactElement => {
  const { t } = useTranslation()
  const isLoadingShown = useDelayedLoading(isLoading)

  // 操作列 sticky の shadow 出し分け (共通 hook。design js と同条件)。
  const { wrapRef, stickyShadow } = useStickyRightShadow([rows, columns])

  const totalColumns =
    columns.length +
    (dragHandleColumn !== undefined ? 1 : 0) +
    (renderRowActions !== undefined ? 1 : 0)
  const hasRowClick = onRowClick !== undefined
  // 空 / loading / error 行だけを表示している状態か (= table を wrap の高さいっぱいに広げる条件)
  const showsMessageRow =
    isLoading === true || errorLabel !== undefined || rows.length === 0

  const messageRow = (msg: string, color?: string): React.ReactElement => (
    <TableMessageRow color={color} colSpan={totalColumns} message={msg} />
  )

  const tdBaseStyle: React.CSSProperties = TABLE_BODY_CELL_STYLE

  const handleActionCellClick = (e: MouseEvent<HTMLTableCellElement>): void => {
    // action col 内のクリックが行クリック (= onRowClick) を発火しないようにする。
    e.stopPropagation()
  }

  const handleDragHandleCellClick = (
    e: MouseEvent<HTMLTableCellElement>,
  ): void => {
    // drag handle セル内のクリックが行クリック (= onRowClick) を発火しないようにする。
    e.stopPropagation()
  }

  /**
   * 「いま drag 可能にしてよい行」 の key。 drag handle 上で mousedown した行だけが
   * `draggable` になる (= 行のどこを掴んでも並べ替えが始まるのを防ぐ)。
   * design 8080 `admin/case-templates.html` の `_disarm()` / mousedown で arm する
   * 実装と同じ考え方。
   */
  const [armedRowKey, setArmedRowKey] = useState<string | null>(null)

  // drag が成立した時は `dragend` で解除するが、 **掴んで drag せずに離した時は
  // `dragend` が来ない**。 その解除をここで行う。 行の `mouseUp` では表の外や
  // window の外で離した場合に届かず (= arm が残り、 次に行のどこを掴んでも並べ替えが
  // 始まる)、 window が focus を失ったまま離される経路もあるので document / window
  // で見る。 drag 中は mouse event 自体が発火しないため正常な drag は潰さない。
  useEffect(() => {
    if (armedRowKey === null) return undefined
    const disarm = (): void => {
      setArmedRowKey(null)
    }
    document.addEventListener('mouseup', disarm)
    window.addEventListener('blur', disarm)
    return () => {
      document.removeEventListener('mouseup', disarm)
      window.removeEventListener('blur', disarm)
    }
  }, [armedRowKey])

  return (
    <Box
      ref={wrapRef}
      position="relative"
      flex="1"
      // 横スクロール対応 (design `.ecl-table-wrap { overflow-x: auto }` 準拠)。
      // 列が多く幅が container を超える表 (例: 需要想定一覧 12 列) で右端列が
      // クリップされず到達できるようにする。 収まる表ではスクロールバーは出ない (= auto)。
      overflowX="auto"
      overflowY="auto"
      background="Surface.primary"
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      // UI 辞書 components/table.md §0 `.ecl-table-wrap` の角丸 (--radius-lg = 8px)
      borderRadius="lg"
      // design `.ecl-table-wrap` と同じ inline-size コンテナ。 空状態行の内側 Box が
      // `100cqi` (= 見えている幅) を参照して中央寄せするために必要。
      // block 軸は含めない (`container-type: size` は過去に wrap 0px 化を起こした)。
      aria-busy={isLoading}
      style={{ containerType: 'inline-size' }}
    >
      <TableRoot
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          // 空 / loading / error の時だけ wrap の確定高さいっぱいに広げて中央寄せする。
          // 確定高さが無ければ height:100% は auto に落ち、 min-height 88px になる。
          height: showsMessageRow ? '100%' : undefined,
        }}
      >
        <TableHeader>
          <TableRow>
            {dragHandleColumn !== undefined && (
              <TableColumnHeader
                key={dragHandleColumn.key}
                style={{
                  ...TABLE_HEADER_CELL_STYLE,
                  textAlign: 'center',
                  width: dragHandleColumn.width ?? '36px',
                }}
              >
                {dragHandleColumn.header ?? null}
              </TableColumnHeader>
            )}
            {columns.map((col) => (
              <TableColumnHeader
                key={col.key}
                aria-sort={col.ariaSort}
                style={{
                  ...TABLE_HEADER_CELL_STYLE,
                  textAlign: textAlignFor(col),
                }}
              >
                {col.header}
              </TableColumnHeader>
            ))}
            {renderRowActions !== undefined && (
              <TableColumnHeader
                // design 正 (`design/CLAUDE.md`「操作列は右揃え固定・TH ラベルなし」/
                // `demand/cases.html` の `<th class="col-detail" aria-label="詳細">`):
                // 見出し文言は出さず、 支援技術向けに aria-label だけ持たせる。
                aria-label={t('common:table.actionsLabel')}
                // 影は inline style ではなく globalCss の `::before` が描く
                // (border-collapse 表では td/th 直当ての box-shadow が paint されない)。
                data-sticky-shadow={stickyShadow}
                style={{
                  position: 'sticky',
                  top: 0,
                  ...stickyActionHeaderStyle(TABLE_HEADER_BG),
                  paddingBlock: '10px',
                  // UI 辞書 components/table.md「列揃えの規約」: 操作列は右揃え固定・TH ラベルなし
                  textAlign: 'right',
                  width: actionColWidth,
                  borderBottom: `1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`,
                }}
              />
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            // 行を出さずに高さだけ保つ (影 + スピナーは表の外枠に重ねる)。読み込み中に空状態を出さない
            <TableRow aria-hidden>
              <TableCell
                height={TABLE_MESSAGE_ROW_MIN_HEIGHT}
                padding={0}
                colSpan={totalColumns}
              />
            </TableRow>
          ) : errorLabel !== undefined ? (
            messageRow(
              errorLabel,
              LightModeSemanticColors.Text.negativeMid.value,
            )
          ) : rows.length === 0 ? (
            messageRow(emptyLabel ?? t('common:table.empty'))
          ) : (
            rows.map((row, idx) => {
              const disabled = isRowDisabled !== undefined && isRowDisabled(row)
              const rowStyle = getRowStyle?.(row)
              const hasDragHandle = dragHandleColumn !== undefined
              return (
                <TableRow
                  key={getRowKey(row)}
                  background={idx % 2 === 1 ? 'Surface.secondary' : undefined}
                  _hover={
                    hasRowClick && !disabled
                      ? {
                          background: 'Hover.lowEmphasis',
                          ...STICKY_ACTION_HOVER_OVERLAY,
                        }
                      : undefined
                  }
                  // zebra は inline style ではなく style props で当てる。
                  // inline style は chakra が生成する class より強く、`_hover` の
                  // 行背景が奇数行だけ効かなくなる (操作列の重ね塗りだけが乗って
                  // 行全体とちぐはぐに見える) ため。
                  draggable={canDragRows && armedRowKey === getRowKey(row)}
                  // 行全体を drag source にする (design 8080
                  // `admin/case-templates.html` と同じ)。 `draggable` をハンドルの
                  // セルに付けると **掴んだセルだけが drag image** になり、
                  // 行が動いて見えない (2026-08-24 デザイナー指摘)。
                  //
                  // ただし常時 `draggable` にすると行のどこを掴んでも並べ替えが
                  // 始まり、 行クリック (= 編集を開く) と衝突する。 そこで 8080 と
                  // 同じく **ハンドルの上で mousedown した時だけ arm する**。
                  // `dragstart` の `e.target` は掴んだ子要素ではなく drag source の
                  // 行そのものなので、 dragstart 側では起点を判定できない。
                  onClick={
                    hasRowClick && !disabled
                      ? (e?: MouseEvent) => {
                          onRowClick(row, e)
                        }
                      : undefined
                  }
                  {...(rowKeyboardActivation && hasRowClick && !disabled
                    ? {
                        tabIndex: 0,
                        _focusVisible: {
                          outlineColor: 'Focus.clarity',
                          outlineOffset: '-2px',
                          outlineStyle: 'solid',
                          outlineWidth: '2px',
                        },
                        onKeyDown: (e: KeyboardEvent<HTMLTableRowElement>) => {
                          if (
                            e.key !== 'Enter' ||
                            e.target !== e.currentTarget ||
                            e.nativeEvent.isComposing ||
                            e.keyCode === 229
                          ) {
                            return
                          }
                          e.preventDefault()
                          onRowClick(row)
                        },
                      }
                    : {})}
                  onDragEnd={
                    hasDragHandle
                      ? () => {
                          setArmedRowKey(null)
                          onRowDragEnd?.()
                        }
                      : undefined
                  }
                  onDragOver={
                    hasDragHandle
                      ? (e: DragEvent<HTMLTableRowElement>) => {
                          e.preventDefault()
                          if (e.dataTransfer !== null) {
                            e.dataTransfer.dropEffect = 'move'
                          }
                          onRowDragOver?.(row, e)
                        }
                      : undefined
                  }
                  onDragStart={
                    hasDragHandle
                      ? (e: DragEvent<HTMLTableRowElement>) => {
                          if (e.dataTransfer !== null) {
                            e.dataTransfer.setData('text/plain', getRowKey(row))
                            e.dataTransfer.effectAllowed = 'move'
                          }
                          onRowDragStart?.(row)
                        }
                      : undefined
                  }
                  onDrop={
                    hasDragHandle
                      ? (e: DragEvent<HTMLTableRowElement>) => {
                          e.preventDefault()
                          onRowDrop?.(row, e)
                        }
                      : undefined
                  }
                  style={{
                    cursor: disabled
                      ? 'not-allowed'
                      : hasRowClick
                        ? 'pointer'
                        : undefined,
                    opacity: disabled ? 0.5 : undefined,
                    ...rowStyle,
                  }}
                >
                  {hasDragHandle && (
                    <TableCell
                      onClick={handleDragHandleCellClick}
                      // ここで掴んだ時だけ行を drag 可能にする (= 行の他の場所を
                      // 掴んでも並べ替えは始まらない)。 解除は行の `dragEnd`
                      // (drag が成立した時) と `mouseUp` (drag せずに離した時)。
                      onMouseDown={
                        canDragRows
                          ? () => {
                              setArmedRowKey(getRowKey(row))
                            }
                          : undefined
                      }
                      style={{
                        ...tdBaseStyle,
                        cursor: canDragRows ? 'grab' : 'not-allowed',
                        textAlign: 'center',
                        width: dragHandleColumn.width ?? '36px',
                      }}
                    >
                      {dragHandleColumn.render(row, {
                        canDrag: canDragRows ?? false,
                      })}
                    </TableCell>
                  )}
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      style={{
                        ...tdBaseStyle,
                        textAlign: textAlignFor(col),
                        // 契約 nowrap / format を td レベルで適用する
                        // (= cell 側で whiteSpace / tabular-nums span を再現しない)。
                        whiteSpace: col.nowrap === true ? 'nowrap' : undefined,
                        fontVariantNumeric:
                          col.format === 'number' ? 'tabular-nums' : undefined,
                      }}
                    >
                      {col.cell(row)}
                    </TableCell>
                  ))}
                  {renderRowActions !== undefined &&
                    (disabled ? (
                      <TableCell
                        data-sticky-action
                        data-sticky-shadow={stickyShadow}
                        style={{
                          ...tdBaseStyle,
                          ...stickyActionCellStyle(idx),
                          width: actionColWidth,
                        }}
                      />
                    ) : (
                      (() => {
                        const actions = renderRowActions(row)
                        return (
                          <TableCell
                            data-sticky-action
                            data-sticky-shadow={stickyShadow}
                            // 中身が無い行 (= renderRowActions が null) では
                            // stopPropagation を付けない。 付けると空セルの領域を
                            // クリックしても行クリック (詳細を開く) が起きなくなる。
                            onClick={
                              actions !== null && actions !== undefined
                                ? handleActionCellClick
                                : undefined
                            }
                            style={{
                              ...tdBaseStyle,
                              ...stickyActionCellStyle(idx),
                              width: actionColWidth,
                            }}
                          >
                            {/* design `.ecl-table td.cell-actions`:
                                      ボタン間 8px (--space-2) / 列の右端寄せ。 */}
                            <HStack
                              justifyContent="flex-end"
                              gap={ROW_ACTION_GAP}
                            >
                              {actions}
                            </HStack>
                          </TableCell>
                        )
                      })()
                    ))}
                </TableRow>
              )
            })
          )}
        </TableBody>
      </TableRoot>
      {isLoadingShown && <RegionLoadingOverlay />}
    </Box>
  )
}

/**
 * 一覧の標準行アクション (編集 + 削除) を `renderRowActions` へ渡す render 関数を作る。
 * 各 table が `renderRowActions={(row) => <><EditIconButton/><DeleteIconButton/></>}` を
 * 再宣言するのを 1 箇所へ集約する。 module 直下で定義するため
 * `react/no-unstable-nested-components` の対象 (component 内 nested component) にならず、
 * 呼び出し側は `renderRowActions={standardRowActions(onEdit, onDelete)}` と書ける。
 */
export const standardRowActions =
  <T,>(onEdit: (row: T) => void, onDelete: (row: T) => void) =>
  // MasterDataTable が `renderRowActions(row)` として直接呼ぶ render 関数であり、
  // `<Component/>` として使う React component ではないため display-name は不要。
  // 各 table の action ブロックをここへ集約した見返りに disable は 1 箇所で済む。
  // eslint-disable-next-line react/display-name
  (row: T): ReactNode => (
    <>
      <EditIconButton onClick={() => onEdit(row)} />
      <DeleteIconButton onClick={() => onDelete(row)} />
    </>
  )
